/**
 * ================================================================================================
 *  אומדן החיוב הבא — a figure that says, in words, that it is an estimate
 * ================================================================================================
 *
 * Item 3 of the Owner's accumulated list: *"אומדן סכום החיוב הבא."*
 *
 * And the rule this surface is most at risk of breaking:
 *
 *     *"אין להמציא נתונים או להציג אפס במקום תקלה."*
 *
 * ── THE ASSUMPTION IS RENDERED, NOT BURIED ────────────────────────────────────────────────────
 *
 * The server computes the figure from the quantities measured on ONE day, held across every day
 * of the next period, and carries that day back as `assumedFrom`. This panel prints it in the
 * sentence beside the amount. An estimate whose assumption appears only in a tooltip, or only in
 * a service's header, is an estimate a customer reads as a price — and then telephones about.
 *
 * ── NOTHING IS COMPUTED HERE, AND NOTHING IS FORMATTED HERE EITHER ────────────────────────────
 *
 * Not the total, not the VAT, not the per-line amounts, and not the currency symbol. The server
 * sends `amountTotalDisplay` already written as *"1,309.80 ₪"* through `formatAmount`, which is
 * the product's one formatter — *"הצג מחיר מקומי קריא, למשל `100.00 ₪`… לא `390.0000 ILS`"*.
 *
 * THE FIRST DRAFT OF THIS PANEL BUILT THE STRING ITSELF, as `${amountTotal} ${currencyCode}`, and
 * rendered `1309.80 ILS` three lines above a documents table reading `118.00 ₪`. The dom suite
 * could not see it — both are "some text" — and the browser showed it in a second. The same open
 * showed that `panel`, `panel__title`, `panel__lead` and `table`, four class names this file had
 * invented, style nothing at all: the stylesheet has no such rules, so the whole block rendered
 * naked beside its neighbours. Both defects are the same defect, and jsdom can see neither.
 *
 * ── ONE PANEL, TWO ROUTES, AND BOTH IDS ARE LITERAL ───────────────────────────────────────────
 *
 * The office asking about itself and the operator asking about a customer get the SAME answer
 * from the same service, so they get the same panel. Both operation ids are written out rather
 * than composed, because `web-client-choke-point` requires it and the reason it requires it is
 * this: `reachability.test.ts` measures which routes this application can actually reach, and a
 * composed id is a route the measurement cannot see.
 */

import { useCallback, useState } from 'react';

import { DataTable } from './DataTable';
import { ServerRead } from './ServerRead';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import type { EstimateLine } from '../api/contract';

/**
 * WHY AN ESTIMATE IS NOT AVAILABLE, IN THE CUSTOMER'S WORDS — keyed on the server's closed list.
 *
 * `ESTIMATE_REFUSALS` in `services/next-charge-estimate.ts` is the vocabulary, and each entry is
 * a DIFFERENT fact: two of them are somebody's to act on and the rest are ordinary states. A
 * single *"לא ניתן לחשב"* for all fifteen would hide the estate having stopped measuring behind
 * the same sentence as a trial that covers the whole month.
 *
 * AN UNKNOWN REASON SAYS SO. A map that fell behind the server's list would otherwise render an
 * empty sentence beside the words *"אומדן החיוב הבא"*, and an empty sentence there reads as
 * nothing to pay.
 */
const REASON_HE: Readonly<Record<string, string>> = {
  no_subscription: 'אין מנוי פעיל, ולכן אין תקופה הבאה לחשב',
  state_not_billable: 'במצב הנוכחי לא מתוכנן חיוב נוסף',
  unknown_period_kind: 'אורך תקופת החיוב אינו מוגדר במערכת — נדרש בירור',
  nothing_measured_recently: 'לא נרשמה מדידת כמויות בשבוע האחרון — נדרש בירור',
  no_common_anchor_day: 'למדידות אין יום משותף, ולכן אין בסיס אחד לאומדן',
  plan_terms_unreadable: 'תנאי התכנית לתקופה הבאה אינם קריאים — נדרש בירור',
  no_billing_account: 'אין פרטי חיוב לארגון, ולכן לא ניתן לחשב מע״מ',
  in_trial: 'התקופה הבאה כולה בתוך תקופת הניסיון',
  cancelled: 'החידוש בוטל, ולכן לא תיגבה תקופה נוספת',
  nothing_to_bill: 'לפי הכמויות הנוכחיות אין מה לחייב בתקופה הבאה',
  no_vat_rate: 'אין שיעור מע״מ רשום לתקופה הבאה — נדרש בירור',
  unpriced: 'לפריט אחד או יותר אין מחיר בתקופה הבאה — נדרש בירור',
  bad_period: 'תאריכי התקופה הבאה אינם תקינים — נדרש בירור',
  bad_plan_change: 'שינוי תכנית מתוכנן אינו תקין — נדרש בירור',
  missing_days: 'סדרת הכמויות אינה מכסה את התקופה — נדרש בירור',
};

export function NextChargeEstimatePanel({
  tenantUid,
}: {
  /**
   * THE OPERATOR'S CUSTOMER, or `undefined` when the office is asking about itself.
   *
   * It is not an address the office sends: on its own route the server reads the tenant from the
   * caller's bound context, which is why that call carries no parameter at all.
   */
  readonly tenantUid?: string;
}): JSX.Element {
  const read = useCallback(
    () =>
      tenantUid === undefined
        ? callOperation('tenant.billing.estimate.read', {})
        : callOperation('platform.tenant.estimate.read', { params: { tenant_uid: tenantUid } }),
    [tenantUid],
  );
  const estimate = useServerData(read);
  const [open, setOpen] = useState(false);

  return (
    <section className="notice" data-area="next-charge-estimate">
      <h2 className="notice__title">אומדן החיוב הבא</h2>
      <ServerRead result={estimate.result} whatHe="את אומדן החיוב הבא" skeletonRows={2}>
        {(data) =>
          data.available === false ? (
            <p className="notice__body" data-area="estimate-unavailable">
              {REASON_HE[data.reason] ?? `האומדן אינו זמין (${data.reason})`}
              {data.detail === null ? null : (
                <>
                  {' '}
                  <span className="field__hint">{data.detail}</span>
                </>
              )}
            </p>
          ) : (
            <>
              <p className="notice__body" data-area="estimate-total">
                <strong dir="ltr">{data.amountTotalDisplay ?? data.amountTotal}</strong>
                {' כולל מע״מ, עבור '}
                <span dir="ltr">{`${data.periodStart} – ${data.periodEnd}`}</span>
              </p>
              {/*
                THE SENTENCE THAT KEEPS THIS AN ESTIMATE. It names the day the quantities were
                read on and states what was assumed about every other day, because that is the
                only thing separating this figure from a price.
              */}
              <p className="notice__body" data-area="estimate-assumption">
                {`זהו אומדן ולא חיוב. הוא מחושב לפי הכמויות שנמדדו ב־${data.assumedFrom}, בהנחה שהן לא ישתנו עד תום התקופה. `}
                {'שינוי בכמויות, בתכנית או בהטבות ישנה את הסכום, והחיוב בפועל מחושב בתום התקופה.'}
              </p>
              <p className="notice__body" data-area="estimate-vat">
                {`לפני מע״מ ${data.amountExVatDisplay ?? data.amountExVat} · מע״מ ${data.vatRatePct}% ${
                  data.vatAmountDisplay ?? data.vatAmount
                }`}
              </p>
              <div className="actions-row">
                <button type="button" className="btn" onClick={() => setOpen(!open)}>
                  {open ? 'הסתרת הפירוט' : 'פירוט החישוב'}
                </button>
              </div>
              {!open ? null : <EstimateBreakdown lines={data.lines} />}
            </>
          )
        }
      </ServerRead>
    </section>
  );
}

/**
 * THE PER-LINE BREAKDOWN, THROUGH `DataTable` AND NOT A TABLE OF ITS OWN.
 *
 * The first draft wrote its own `<table>`, which is the design-system divergence the UX contract
 * exists to refuse: `DataTable` owns the sticky identity column, the single horizontal
 * scrollport (T4) and the empty state that does not read as a failure (T12), and a second table
 * in this product is a second set of answers to all three.
 */
function EstimateBreakdown({ lines }: { readonly lines: readonly EstimateLine[] }): JSX.Element {
  return (
    <DataTable
      captionHe="פירוט החישוב לתקופה הבאה"
      emptyStateHe="אין פריטים לחיוב בתקופה הבאה"
      rows={lines}
      rowKey={(row) => row.skuCode}
      columns={[
        { key: 'sku', headerHe: 'פריט', render: (row) => <span>{row.displayNameHe}</span> },
        {
          key: 'quantity',
          headerHe: 'כמות',
          numeric: true,
          render: (row) => <span dir="ltr">{row.quantity}</span>,
        },
        {
          key: 'days',
          headerHe: 'ימים',
          numeric: true,
          render: (row) => <span dir="ltr">{`${row.chargedDays}/${row.periodDays}`}</span>,
        },
        {
          key: 'gross',
          headerHe: 'לפני הטבה',
          numeric: true,
          render: (row) => <span dir="ltr">{row.grossAmountDisplay ?? '—'}</span>,
        },
        {
          key: 'benefit',
          headerHe: 'הטבה',
          numeric: true,
          /*
            AN EM-DASH WHERE THERE IS NO BENEFIT, never `0.00 ₪` — 09 §9.2, and the same choice
            the documents table makes about a missing document number. A figure of zero and the
            absence of a figure are two different facts, and a reader who sees the first assumes
            a benefit was applied and came to nothing, and then asks which one.
          */
          render: (row) => <span dir="ltr">{row.benefitAmountDisplay ?? '—'}</span>,
        },
        {
          key: 'amount',
          headerHe: 'לתשלום',
          numeric: true,
          render: (row) => <span dir="ltr">{row.amountDisplay ?? '—'}</span>,
        },
      ]}
    />
  );
}
